/*
 * Horizontal bar chart. Three columns: label · track · count·pct meta.
 *
 * Pure CSS — no external chart lib (matches the prototype). Bar widths
 * are scaled to the largest item in the list so even small numbers
 * render cleanly. The `total` is used only for the percentage meta.
 */

import type { CountedItem } from '../types';
import { fmtPct } from '../format';

export type BarTone = 'default' | 'warn' | 'accent';

interface BarChartProps {
  items: CountedItem[];
  total: number;
  tone?: BarTone;
  /** Optional label remap — useful for keys like "setup" -> "Setup". */
  labelMap?: Readonly<Record<string, string>>;
  /** Custom row tone per item (e.g. PE-owned ownership rows = warn). */
  toneFor?: (item: CountedItem) => BarTone | undefined;
  /** Override the per-row count suffix (e.g. "vendors"). */
  countSuffix?: (item: CountedItem) => string | null;
  emptyMessage?: string;
}

const TONE_CLASS: Record<BarTone, string> = {
  default: '',
  warn: 'bar-fill--warn',
  accent: 'bar-fill--accent',
};

export function BarChart({
  items,
  total,
  tone = 'default',
  labelMap,
  toneFor,
  countSuffix,
  emptyMessage,
}: BarChartProps) {
  if (items.length === 0) {
    return (
      <p style={{ fontSize: 12, color: 'var(--textMuted)', marginTop: 8 }}>
        {emptyMessage ?? 'No data.'}
      </p>
    );
  }
  const max = Math.max(1, ...items.map((it) => it.count));
  return (
    <div className="bars" role="list">
      {items.map((it) => {
        const w = (it.count / max) * 100;
        const pct = fmtPct(it.count, total);
        const rowTone = toneFor?.(it) ?? tone;
        const label = labelMap?.[it.key] ?? it.key;
        const suffix = countSuffix?.(it);
        return (
          <div className="bar-row" key={it.key} role="listitem">
            <div className="bar-label" title={label}>
              {label}
            </div>
            <div className="bar-track">
              <div
                className={`bar-fill ${TONE_CLASS[rowTone]}`}
                style={{ width: `${Math.max(w, 2)}%` }}
                aria-hidden
              />
            </div>
            <div className="bar-meta">
              {it.count}
              {suffix ? ` ${suffix}` : ''} · {pct}%
            </div>
          </div>
        );
      })}
    </div>
  );
}
